import { useRouter } from 'next/router'
import { stringify } from 'querystring'
import { useEffect } from 'react'
import {
  MAX_CAR_PAYOUT,
  MIN_CAR_PAYOUT,
} from '@/blocks/CarPawnHero/LoanAmountCard'
import { useSearchParams } from '@/helpers/useSearchParams'

export type CarFormParams = {
  betrag: {
    betrag: number
  }
  marke: {
    marke: string
  } & CarFormParams['betrag']
  modell: {
    modell: string
  } & CarFormParams['marke']
  zulassungsjahr: {
    zulassungsjahr: RegistrationYear
  } & CarFormParams['modell']
}

type ParseStep<K extends keyof CarFormParams> = ParseResult<K>

type ParseResult<K extends keyof CarFormParams> = ParseSuccess<K> | ParseError

type ParseError = [
  undefined,
  {
    redirect: string
  },
]

type ParseSuccess<K extends keyof CarFormParams> = [CarFormParams[K], undefined]

function isParseError<K extends keyof CarFormParams>(
  result: ParseResult<K>,
): result is ParseError {
  return result[1] !== undefined && 'redirect' in result[1]
}

export function useRedirectOnParseError(
  result: ParseResult<keyof CarFormParams>,
) {
  const { push } = useRouter()

  useEffect(() => {
    if (isParseError(result)) {
      push(result[1].redirect)
      return
    }
  }, [push, result])
}

export function useParsePayoutAmount(): ParseStep<'betrag'> {
  const { betrag } = useSearchParams()
  return parsePayoutAmount(betrag)
}

export function parsePayoutAmount(
  betrag: string | string[] | null | undefined,
): ParseStep<'betrag'> {
  const payoutAmount =
    typeof betrag === 'string' ? Number.parseInt(betrag) : undefined

  if (
    payoutAmount === undefined ||
    Number.isNaN(payoutAmount) ||
    payoutAmount < MIN_CAR_PAYOUT ||
    payoutAmount > MAX_CAR_PAYOUT
  ) {
    return [undefined, { redirect: '/autopfand' }]
  } else {
    return [{ betrag: payoutAmount }, undefined]
  }
}

export function useParseMake(): ParseStep<'marke'> {
  const [payoutAmount, payoutAmountRedirect] = useParsePayoutAmount()
  const { marke } = useSearchParams()

  if (payoutAmountRedirect) {
    return [undefined, { redirect: payoutAmountRedirect.redirect }]
  } else {
    return parseMake(marke, payoutAmount.betrag)
  }
}

export function parseMake(
  make: string | string[] | null | undefined,
  betrag: number,
): ParseStep<'marke'> {
  if (typeof make !== 'string') {
    return [
      undefined,
      { redirect: '/autopfand-formular?' + stringify({ betrag }) },
    ]
  } else {
    return [{ marke: make, betrag }, undefined]
  }
}

export function useParseModel(): ParseStep<'modell'> {
  const [payoutAmount, payoutAmountRedirect] = useParsePayoutAmount()
  const [make, makeRedirect] = useParseMake()
  const { modell } = useSearchParams()

  if (payoutAmountRedirect) {
    return [undefined, { redirect: payoutAmountRedirect.redirect }]
  } else if (makeRedirect) {
    return [undefined, { redirect: makeRedirect.redirect }]
  } else if (typeof modell !== 'string') {
    return [
      undefined,
      {
        redirect:
          '/autopfand-formular/modell?' +
          stringify({ betrag: payoutAmount.betrag, marke: make.marke }),
      },
    ]
  } else {
    return [{ modell, ...make }, undefined]
  }
}

export function useParseRegistrationYear(): ParseStep<'zulassungsjahr'> {
  const [payoutAmount, payoutAmountRedirect] = useParsePayoutAmount()
  const [make, makeRedirect] = useParseMake()
  const [model, modelRedirect] = useParseModel()
  const { zulassungsjahr } = useSearchParams()

  const registrationYear =
    typeof zulassungsjahr === 'string'
      ? parseRegistrationYearFromString(zulassungsjahr)
      : undefined

  if (payoutAmountRedirect) {
    return [undefined, { redirect: payoutAmountRedirect.redirect }]
  } else if (makeRedirect) {
    return [undefined, { redirect: makeRedirect.redirect }]
  } else if (modelRedirect) {
    return [undefined, { redirect: modelRedirect.redirect }]
  } else if (registrationYear === undefined) {
    return [
      undefined,
      {
        redirect:
          '/autopfand-formular/zulassungsjahr?' +
          stringify({
            betrag: payoutAmount.betrag,
            marke: make.marke,
            modell: model.modell,
          }),
      },
    ]
  } else {
    return [{ zulassungsjahr: registrationYear, ...model }, undefined]
  }
}

export type RegistrationYear = number | 'before-range'

function parseRegistrationYearFromString(raw: string): RegistrationYear {
  const year = Number.parseInt(raw)
  if (Number.isNaN(year) || !Number.isFinite(year) || !Number.isInteger(year)) {
    return 'before-range'
  }

  if (
    year > new Date().getFullYear() ||
    year < MIN_SELECTABLE_REGISTRATION_YEAR
  ) {
    return 'before-range'
  }

  return year
}

// this is also checked in the CF request function
export const MIN_SELECTABLE_REGISTRATION_YEAR = new Date().getFullYear() - 13
